import { useState, useEffect, createContext, useContext } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuth, useLogo, API } from "../App";
import { Button } from "./ui/button";
import { Clock, Users, Calendar, Settings, LogOut, Menu, X, LayoutDashboard, CreditCard, Crown, CalendarDays, Database, Calculator, FileSpreadsheet, CalendarCheck, BookOpen, MessageCircle, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// Plan Context for sharing plan features across components
const PlanContext = createContext(null);
export const usePlan = () => useContext(PlanContext);

const AdminLayout = () => {
  const { user, logout } = useAuth();
  const { logoUrl } = useLogo();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [planData, setPlanData] = useState(null);

  useEffect(() => {
    // Check if user is super admin
    setIsSuperAdmin(user?.role === "super_admin");
    
    // Fetch plan data
    const fetchPlanData = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`);
        setPlanData({
          company: res.data.company,
          plan: res.data.plan,
          features: res.data.plan?.features || { face_auth: true }
        });
      } catch (error) {
        console.error("Failed to fetch plan data:", error);
        // Default to allowing face_auth if fetch fails
        setPlanData({ features: { face_auth: true } });
      }
    };
    
    if (user) {
      fetchPlanData();
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    toast.success("ログアウトしました");
    navigate("/");
  };

  const navItems = [
    { to: "/admin", icon: <LayoutDashboard className="w-5 h-5" />, label: "ダッシュボード", exact: true },
    { to: "/admin/employees", icon: <Users className="w-5 h-5" />, label: "従業員管理" },
    { to: "/admin/attendance", icon: <Calendar className="w-5 h-5" />, label: "勤怠記録" },
    { to: "/admin/shifts", icon: <CalendarDays className="w-5 h-5" />, label: "シフト管理" },
    { to: "/admin/salary", icon: <Calculator className="w-5 h-5" />, label: "給与計算" },
    { to: "/admin/wage-ledger", icon: <FileSpreadsheet className="w-5 h-5" />, label: "賃金台帳" },
    { to: "/admin/year-end-adjustment", icon: <CalendarCheck className="w-5 h-5" />, label: "年末調整" },
    { to: "/admin/masters", icon: <Database className="w-5 h-5" />, label: "マスタ設定" },
    { to: "/admin/settings", icon: <Settings className="w-5 h-5" />, label: "設定" },
    { to: "/admin/whatsapp", icon: <MessageCircle className="w-5 h-5" />, label: "WhatsApp通知" },
    { to: "/admin/line", icon: <MessageSquare className="w-5 h-5" />, label: "LINE通知" },
    { to: "/admin/subscription", icon: <CreditCard className="w-5 h-5" />, label: "サブスクリプション" },
    { to: "/admin/manual", icon: <BookOpen className="w-5 h-5" />, label: "マニュアル" }
  ];

  // Add super admin link if user is super admin
  if (isSuperAdmin) {
    navItems.push({ to: "/admin/super-admin", icon: <Crown className="w-5 h-5" />, label: "スーパー管理" });
  }

  return (
    <div className="min-h-screen bg-slate-900 text-white dark">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-slate-800 border-b border-slate-700">
        <div className="flex items-center justify-between px-4 h-14">
          <button 
            onClick={() => setSidebarOpen(true)}
            className="p-2 hover:bg-slate-700 rounded-lg"
          >
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2">
            <img src={logoUrl} alt="PRC勤怠" className="w-6 h-6 object-contain rounded-lg" />
            <span className="font-bold">PRC勤怠</span>
          </div>
          <div className="w-9" />
        </div>
      </header>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="lg:hidden fixed inset-0 z-50 bg-black/60"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-slate-800 border-r border-slate-700
        transform transition-transform duration-200
        lg:translate-x-0 ${sidebarOpen ? "translate-x-0" : "-translate-x-full"}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-4 h-14 border-b border-slate-700">
            <div className="flex items-center gap-2">
              <img src={logoUrl} alt="PRC勤怠" className="w-6 h-6 object-contain rounded-lg" />
              <span className="font-bold">PRC勤怠</span>
            </div>
            <button 
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden p-1 hover:bg-slate-700 rounded"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-3 space-y-1">
            {navItems.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.exact}
                onClick={() => setSidebarOpen(false)}
                className={({ isActive }) => `
                  flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm
                  ${isActive 
                    ? "bg-emerald-600 text-white" 
                    : "text-slate-300 hover:bg-slate-700"
                  }
                `}
              >
                {item.icon}
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>

          {/* User */}
          <div className="p-3 border-t border-slate-700">
            <div className="flex items-center gap-2 mb-3 px-2">
              <div className="w-8 h-8 rounded-full bg-slate-600 flex items-center justify-center text-sm">
                {user?.name?.charAt(0) || "A"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.name || "管理者"}</p>
                <p className="text-xs text-slate-400 truncate">{user?.email}</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              className="w-full border-slate-600 hover:bg-slate-700 text-slate-300" 
              onClick={handleLogout}
            >
              <LogOut className="w-4 h-4 mr-2" />
              ログアウト
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64">
        <div className="max-w-6xl mx-auto px-4 py-6">
          <PlanContext.Provider value={planData}>
            <Outlet />
          </PlanContext.Provider>
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
